import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { enCA, enUS, frCA } from 'date-fns/locale';
import DatePicker, { registerLocale } from 'react-datepicker';
import moment from 'moment';

import {
  ReactComponent as Calendar,
} from '../../assets/common/calendar.svg';

import i18n from '../../i18n';

import styles from './DatePickerFigo.module.css';

registerLocale('fr-ca', frCA);
registerLocale('en-ca', enCA);
registerLocale('en-us', enUS);

export const DATE_PICKER_TYPE = {
  FULL_DATE: 'full-date',
  MONTH_YEAR: 'month-year',
};

const DatePickerFigo = ({
  autoFormat = false,
  classContainer = '',
  maxDate = new Date(),
  minDate = new Date(),
  name = 'date-picker-figo',
  onDateChange = () => { },
  onBlur = () => { },
  pickerType = DATE_PICKER_TYPE.FULL_DATE,
  selectedDate = null,
  showIcon = true,
}) => {
  const { t } = useTranslation('fileClaimbot');
  const DIGITS_LIMIT = 8;

  const currentLanguage = useMemo(() => (i18n
    .language.toLowerCase()), [i18n.language]);

  const dateFormat = useMemo(() => {
    switch (pickerType) {
      case DATE_PICKER_TYPE.MONTH_YEAR:
        return t('common:dateFormats.monthYear');

      case DATE_PICKER_TYPE.FULL_DATE:
      default:
        return t('common:dateFormats.datePicker');
    }
  }, [t, pickerType]);

  const placeHolderText = useMemo(() => {
    switch (pickerType) {
      case DATE_PICKER_TYPE.MONTH_YEAR:
        return t('common:dateFormats.monthYear');

      case DATE_PICKER_TYPE.FULL_DATE:
      default:
        return t('common:dateFormats.short');
    }
  }, [t, pickerType]);

  const handleDateChange = useCallback((date) => {
    if (!moment(date).isValid()) {
      onDateChange(null);
      return;
    }

    const format = t('common:dateFormats.short');
    const formattedDate = moment(date, format).toISOString();
    onDateChange(autoFormat ? formattedDate : date);
  }, [autoFormat, onDateChange, t]);

  const handleRawChange = useCallback((event) => {
    const input = event?.target;

    if (!input?.value) {
      return;
    }

    // Keep only numbers, limit to 8 digits, add slashes: MM/DD/YYYY
    const digits = input.value.replace(/\D/g, '').slice(0, DIGITS_LIMIT);
    const formatted = digits
      .replace(/^(\d{2})(\d)/, '$1/$2')
      .replace(/^(\d{2}\/\d{2})(\d)/, '$1/$2');

    input.value = formatted;

    const wholeDateTyped = /(\d{2})\/(\d{2})\/(\d{4})/;
    if (wholeDateTyped.test(formatted)) {
      handleDateChange(formatted);
    }
  }, [handleDateChange]);

  return (
    <DatePicker
      autoComplete="off"
      className={`${styles.input} ${classContainer}`}
      dateFormat={dateFormat}
      icon={<Calendar className={styles.icon} />}
      id={name}
      locale={currentLanguage}
      maxDate={new Date(maxDate)}
      minDate={new Date(minDate)}
      name={name}
      onBlur={onBlur}
      onChange={handleDateChange}
      onChangeRaw={autoFormat && pickerType === DATE_PICKER_TYPE.FULL_DATE
        ? handleRawChange
        : undefined}
      placeholderText={placeHolderText}
      selected={selectedDate}
      showIcon={showIcon}
      showMonthYearPicker={pickerType === DATE_PICKER_TYPE.MONTH_YEAR}
    />
  );
};

const DatePickerFigoMemo = React.memo(DatePickerFigo);

export { DatePickerFigoMemo as DatePickerFigo };
